<template>
  <div class="template-ai">
    <div class="template-ai__header">
      <svg class="template-ai__spark" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M9 3.5c.2 0 .38.13.44.32l1.06 3.6c.33 1.1 1.19 1.96 2.29 2.29l3.6 1.06a.46.46 0 0 1 0 .88l-3.6 1.06a3.44 3.44 0 0 0-2.29 2.29l-1.06 3.6a.46.46 0 0 1-.88 0l-1.06-3.6a3.44 3.44 0 0 0-2.29-2.29l-3.6-1.06a.46.46 0 0 1 0-.88l3.6-1.06a3.44 3.44 0 0 0 2.29-2.29l1.06-3.6A.46.46 0 0 1 9 3.5Z" />
        <path d="M18 2c.14 0 .27.09.31.23l.5 1.7c.19.63.68 1.12 1.31 1.31l1.7.5a.33.33 0 0 1 0 .62l-1.7.5c-.63.19-1.12.68-1.31 1.31l-.5 1.7a.33.33 0 0 1-.62 0l-.5-1.7a1.97 1.97 0 0 0-1.31-1.31l-1.7-.5a.33.33 0 0 1 0-.62l1.7-.5c.63-.19 1.12-.68 1.31-1.31l.5-1.7A.33.33 0 0 1 18 2Z" />
      </svg>
      <span class="template-ai__title">Generate with Biku</span>
      <span class="template-ai__beta">Beta</span>
      <i class="tim-icons icon-simple-remove template-ai__close" title="Hide" @click="$emit('close')"></i>
    </div>

    <el-input
      type="textarea"
      :rows="2"
      v-model="prompt"
      :disabled="loading"
      resize="none"
      :placeholder="hasContent ? 'Describe what to add, e.g. &quot;a testimonial section with a booking button&quot;' : 'Describe the email content, e.g. &quot;a 20% off comeback offer for customers we have not seen in a while&quot;'"
      @keydown.native.meta.enter="submit"
      @keydown.native.ctrl.enter="submit" />

    <div class="template-ai__actions">
      <small v-if="error" class="template-ai__error">{{ error }}</small>
      <small v-else-if="loading" class="template-ai__hint">Biku is designing. Your template saves first; this can take up to a minute.</small>
      <small v-else class="template-ai__hint">{{ hasContent ? 'Biku can add new sections or update existing ones, like changing a button color or rewording a heading.' : 'Biku builds this template\'s content. You can edit everything after.' }}</small>
      <base-button type="primary" size="sm" class="template-ai__btn" :disabled="loading || !prompt.trim()" @click="submit">
        <span v-if="loading"><i class="el-icon-loading"></i> Designing…</span>
        <span v-else>Generate</span>
      </base-button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'

const props = withDefaults(defineProps<{
  // Whether the template already has content; wording only, the builder owns
  // all generation logic.
  hasContent?: boolean
  loading?: boolean
  error?: string
}>(), { hasContent: false, loading: false, error: '' })

const emit = defineEmits<{
  (e: 'submit', prompt: string): void
  (e: 'close'): void
}>()

const prompt = ref('')

function submit() {
  if (props.loading || !prompt.value.trim()) return
  emit('submit', prompt.value.trim())
}
</script>

<style scoped>
.template-ai {
  background: #171826;
  border-bottom: 1px solid #393c54;
  padding: 0.65rem 1rem 0.85rem;
}

.template-ai__header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}

.template-ai__spark {
  width: 20px;
  height: 20px;
  color: #e14eca;
}

.template-ai__title {
  color: #ffffff;
  font-size: 0.875rem;
  font-weight: 600;
}

.template-ai__beta {
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #e14eca;
  background: rgba(225, 78, 202, 0.15);
  border: 1px solid rgba(225, 78, 202, 0.3);
  border-radius: 4px;
  padding: 2px 6px;
  line-height: 1;
}

.template-ai__close {
  margin-left: auto;
  cursor: pointer;
  font-size: 12px !important;
  color: rgba(125, 125, 125, 0.9) !important;
}

.template-ai__close:hover {
  color: #e14eca !important;
}

.template-ai__actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 10px;
}

.template-ai__btn {
  flex-shrink: 0;
  margin: 0;
}

.template-ai__error {
  /* Matches base-input's has-danger state ($danger-states) in both themes */
  color: #ec250d;
}

.template-ai__hint {
  color: rgba(255, 255, 255, 0.6);
  line-height: 1.3;
}
</style>

<!-- Non-scoped: theme the Element UI textarea for both modes, and the band
     surface in light mode. Keyed on .template-ai so it stays local. -->
<style>
.template-ai .el-textarea__inner {
  background: #16161f !important;
  color: #ffffff !important;
  border: 1px solid rgba(255, 255, 255, 0.14) !important;
  border-radius: 0.4285rem;
  font-size: 0.875rem;
  box-shadow: inset 0 2px 6px rgba(0, 0, 0, 0.35);
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}
.template-ai .el-textarea__inner::placeholder {
  color: rgba(255, 255, 255, 0.45) !important;
}
.template-ai .el-textarea__inner:focus {
  border-color: #e14eca !important;
  box-shadow: inset 0 2px 6px rgba(0, 0, 0, 0.35), 0 0 0 3px rgba(225, 78, 202, 0.12);
}
.white-content .template-ai {
  background: #ffffff;
  border-bottom-color: #e3e3e3;
}
.white-content .template-ai__title {
  color: rgba(0, 0, 0, 0.85);
}
.white-content .template-ai__hint {
  color: rgba(0, 0, 0, 0.55);
}
.white-content .template-ai .el-textarea__inner {
  background: #fafafc !important;
  color: rgba(0, 0, 0, 0.85) !important;
  border-color: #dcdce4 !important;
  box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.06);
}
.white-content .template-ai .el-textarea__inner:focus {
  border-color: #e14eca !important;
  box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.06), 0 0 0 3px rgba(225, 78, 202, 0.1);
}
.white-content .template-ai .el-textarea__inner::placeholder {
  color: rgba(0, 0, 0, 0.4) !important;
}
</style>
